import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, TextField, Typography,
} from '@mui/material';
import { requestGabbaiOtp, verifyGabbaiOtp } from '../gabbaiPortalApi';
import { fieldSxFromTheme, getGabbaiTheme } from '../gabbaiPortalTheme';

const RESEND_SECONDS = 45;

export default function LoginScreen({ onSuccess }) {
  const t = getGabbaiTheme();
  const fieldSx = fieldSxFromTheme(t);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('phone'); // phone | code
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const sendOtp = async () => {
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const data = await requestGabbaiOtp(phone.trim());
      setInfo(data.message || 'אם המספר רשום, תתקבל שיחה עם הקוד');
      setStep('code');
      setCooldown(RESEND_SECONDS);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשליחת הקוד');
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await verifyGabbaiOtp(phone.trim(), code.trim());
      onSuccess(data);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאת אימות');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={step === 'code' ? verify : (e) => { e.preventDefault(); sendOtp(); }} sx={{ maxWidth: 420, mx: 'auto' }}>
      <Typography
        variant="h5"
        sx={{
          color: '#f5f0e6',
          fontFamily: '"Frank Ruhl Libre", "David Libre", serif',
          fontWeight: 700,
          mb: 1,
          textAlign: 'center',
        }}
      >
        פורטל גבאים
      </Typography>
      <Typography sx={{ color: 'rgba(245,240,230,0.75)', mb: 3, textAlign: 'center', fontSize: '0.95rem' }}>
        {step === 'phone'
          ? 'הזינו את מספר הטלפון הרשום כגבאי — יישלח קוד בשיחה קולית.'
          : 'הקלידו את הקוד שנאמר בשיחה.'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {info && !error && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          {info}
        </Alert>
      )}

      <TextField
        fullWidth
        type="tel"
        label="מספר טלפון"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        margin="normal"
        required
        disabled={step === 'code' && loading}
        inputProps={{ inputMode: 'tel', pattern: '[0-9]*', dir: 'ltr' }}
        sx={fieldSx}
      />

      {step === 'code' && (
        <TextField
          fullWidth
          type="tel"
          label="קוד אימות"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          margin="normal"
          required
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', dir: 'ltr', maxLength: 6 }}
          sx={fieldSx}
        />
      )}

      <Button
        type="submit"
        fullWidth
        disabled={loading || (step === 'phone' && !phone.trim()) || (step === 'code' && code.length < 6)}
        sx={{
          mt: 3,
          minHeight: 48,
          py: 1.4,
          fontWeight: 700,
          fontSize: { xs: '1rem', sm: '1.05rem' },
          borderRadius: 2,
          color: t.ctaColor,
          background: t.ctaBg,
          boxShadow: `0 4px 20px ${t.accentMuted}`,
          '&:hover': { filter: 'brightness(1.06)' },
          '&.Mui-disabled': { background: t.ctaDisabledBg, color: t.ctaDisabledColor },
        }}
      >
        {loading
          ? <CircularProgress size={24} sx={{ color: t.ctaColor }} />
          : step === 'phone' ? 'שלחו לי קוד בשיחה' : 'כניסה ←'}
      </Button>

      {step === 'code' && (
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button
            type="button"
            fullWidth
            disabled={loading || cooldown > 0}
            onClick={sendOtp}
            sx={{
              minHeight: 44,
              fontWeight: 700,
              borderRadius: 2,
              color: t.accent,
              border: `1px solid ${t.accentBorder}`,
              '&.Mui-disabled': { color: 'rgba(245,240,230,0.35)', borderColor: 'rgba(245,240,230,0.15)' },
            }}
          >
            {cooldown > 0 ? `שלח שוב בעוד ${cooldown}` : 'שלח קוד שוב'}
          </Button>
          <Button
            type="button"
            onClick={() => { setStep('phone'); setCode(''); setInfo(''); setError(''); }}
            sx={{ minHeight: 44, color: 'rgba(245,240,230,0.7)', whiteSpace: 'nowrap' }}
          >
            שנה מספר
          </Button>
        </Box>
      )}

      <Button
        component={RouterLink}
        to="/rimush"
        fullWidth
        sx={{
          mt: 3,
          minHeight: 44,
          fontWeight: 600,
          borderRadius: 2,
          color: 'rgba(245,240,230,0.7)',
          textDecoration: 'underline',
          textUnderlineOffset: 3,
        }}
      >
        כניסת נבחנים
      </Button>
    </Box>
  );
}
